import * as z from "zod";
import { sendPauseReasons, sendStatuses } from "./engagement/enums";
import { sendTestVerdicts } from "./send";

// The report of a send and its live control room (ADR-316 §6, ADR-315): what crm counted exactly in its summaries,
// read with one key each, never by counting events. While the send goes out the room reads `live` again at every signal
// of `crm.send:<id>`, with the cursor of the newest signal it shows; afterwards the report reads the curve of the first
// 72 hours and who is worth a call.

const sendId = z.uuid();
const count = z.number().int();
const instant = z.iso.datetime();

/** The sizes of the live control room: how many human signals its feed shows, and how far a read looks back. */
export const SEND_LIVE = {
  feedSize: 30,
  /** A signal the queue delivered after a newer one still shows: each read looks this far before its cursor. */
  feedLookbackSeconds: 120,
  /** A batch of the Workflow, for the estimate of the end: the pause between two batches and the emails in flight. */
  secondsPerBatch: 4,
} as const;

/** The people of the report worth a call: at most this many, the latest to move first. */
export const SEND_REPORT = { worthACall: 10 } as const;

/** What the feed "Right now" shows: human signals only (ADR-316 §2), the bookings that come from booking included. */
export const sendFeedKinds = ["click", "open", "link_opened", "slots_viewed", "booked"] as const;
export type SendFeedKind = (typeof sendFeedKinds)[number];

/** Why a person is worth a call: the furthest they went without booking (ADR-316 §3). */
export const worthACallReasons = ["saw_times", "opened_link", "clicked"] as const;
export type WorthACallReason = (typeof worthACallReasons)[number];

export const getCrmSendLiveInput = z.object({
  sendId,
  /** The newest signal the screen shows (`cursor` of the last answer): only what came after, and a little before. */
  since: instant.optional(),
});
export type GetCrmSendLiveInput = z.input<typeof getCrmSendLiveInput>;

export const getCrmSendReportInput = z.object({ sendId });
export type GetCrmSendReportInput = z.input<typeof getCrmSendReportInput>;

/** The exact totals of a send (`crm.send_totals`): human and raw side by side (ADR-313 §6). */
export const sendTotalsDto = z
  .object({
    delivered: count,
    bouncedHard: count,
    bouncedSoft: count,
    rejected: count,
    complained: count,
    unsubscribed: count,
    opensRaw: count,
    opensHuman: count,
    /** Counted automatically by Apple Mail. */
    opensMpp: count,
    /** The people whose email was opened, among those measured. */
    opensUnique: count,
    /** A human click without an open measured: the email was opened. */
    opensInferred: count,
    clicksRaw: count,
    clicksHuman: count,
    clicksBot: count,
    clicksUniqueHuman: count,
    /** The steps of the personal booking links of the send, once per link (ADR-313 §4). */
    linkOpened: count,
    slotsViewed: count,
    booked: count,
  })
  .meta({ id: "SendTotals" });
export type SendTotalsDto = z.output<typeof sendTotalsDto>;

/** How far the Workflow of the send went, batch after batch, and when it should end. */
export const sendProgressDto = z
  .object({
    status: z.enum(sendStatuses),
    pauseReason: z.enum(sendPauseReasons).nullable(),
    version: count,
    recipientCount: count,
    sentCount: count,
    skippedCount: count,
    failedCount: count,
    /** The emails sent at the level `full`: what the opens are measured on (ADR-314 §1). */
    measuredCount: count,
    batchesDone: count,
    batches: count,
    startedAt: instant.nullable(),
    sentAt: instant.nullable(),
    cancelledAt: instant.nullable(),
    /** When the next wave leaves, if one is still ahead. */
    nextWaveAt: instant.nullable(),
    /** When the last batch should be out, from the waves and the pace: an estimate; null when nothing is left. */
    etaAt: instant.nullable(),
  })
  .meta({ id: "SendProgress" });
export type SendProgressDto = z.output<typeof sendProgressDto>;

/** A minute of the first 3 hours (`crm.send_stats_minute`): emails sent, delivered, human clicks, bookings. */
export const sendMinuteDto = z.object({ at: instant, sent: count, delivered: count, clicked: count, booked: count });
export type SendMinuteDto = z.output<typeof sendMinuteDto>;

/** An hour of the first 72 (`crm.send_stats_hourly`): opens and clicks of real people, and everything recorded. */
export const sendHourDto = z.object({
  at: instant,
  opensHuman: count,
  opensAll: count,
  clicksHuman: count,
  clicksAll: count,
});
export type SendHourDto = z.output<typeof sendHourDto>;

/** A human signal of the send, the newest first. */
export const sendFeedItemDto = z
  .object({
    id: z.uuid(),
    at: instant,
    kind: z.enum(sendFeedKinds),
    /**
     * Who, only where the level of measure keeps the person (an open only at `full`, ADR-314 §1); null otherwise, and
     * never to a delegated access.
     */
    person: z
      .object({
        contactPersonId: z.uuid(),
        name: z.string(),
        accountOrgId: z.uuid().nullable(),
        accountName: z.string(),
      })
      .nullable()
      .optional(),
    /** The words of the link clicked, when it has any. */
    link: z.string().nullable(),
  })
  .meta({ id: "SendFeedItem" });
export type SendFeedItemDto = z.output<typeof sendFeedItemDto>;

/** What the live control room reads at every signal of the room of the send (ADR-316 §6). */
export const crmSendLiveDto = z
  .object({
    progress: sendProgressDto,
    totals: sendTotalsDto,
    /** Every minute from the start to now, in the first 3 hours. */
    minutes: z.array(sendMinuteDto),
    /** The human signals after the cursor (and a little before it: the screen keeps each once, by id). */
    feed: z.array(sendFeedItemDto),
    /** The newest signal read so far: the `since` of the next read. */
    cursor: instant.nullable(),
  })
  .meta({ id: "CrmSendLive" });
export type CrmSendLiveDto = z.output<typeof crmSendLiveDto>;

/** A person who moved without booking, with the furthest step and when they last moved. */
export const worthACallDto = z
  .object({
    contactPersonId: z.uuid(),
    name: z.string(),
    accountOrgId: z.uuid(),
    accountName: z.string(),
    reason: z.enum(worthACallReasons),
    lastAt: instant,
    clicks: count,
  })
  .meta({ id: "WorthACall" });
export type WorthACallDto = z.output<typeof worthACallDto>;

/** A variant of the A/B test of a send (ADR-324 §2): its emails, the human clicks, the bookings and their rate. */
export const sendTestVariantDto = z.object({
  variant: z.enum(["a", "b"]),
  sent: count,
  delivered: count,
  clicked: count,
  booked: count,
  /** Bookings on emails delivered, 0 to 1; null before a delivery. */
  bookingRate: z.number().nullable(),
});
export type SendTestVariantDto = z.output<typeof sendTestVariantDto>;

/**
 * The A/B test of a send, read again at every read (ADR-324 §2): the two variants now, the verdict (`pending` until it
 * decides, then what the decision saw: a winner, a tie or "too early to say"), when it decides or decided, and which
 * variant the rest received. Counts only, never who.
 */
export const sendTestResultDto = z
  .object({
    element: z.enum(["subject", "preheader", "hero"]),
    variants: z.array(sendTestVariantDto),
    verdict: z.enum(sendTestVerdicts),
    winner: z.enum(["a", "b"]).nullable(),
    decideAt: instant.nullable(),
    decidedAt: instant.nullable(),
  })
  .meta({ id: "SendTestResult" });
export type SendTestResultDto = z.output<typeof sendTestResultDto>;

/** The overview of the report of a send (ADR-315 §3): the totals, the curve of the first 72 hours, who to call. */
export const crmSendReportDto = z
  .object({
    progress: sendProgressDto,
    totals: sendTotalsDto,
    /** Every hour from the start to now, in the first 72. */
    hours: z.array(sendHourDto),
    /** The A/B test, for a send with one (ADR-324 §2). */
    test: sendTestResultDto.nullable().optional(),
    /** The people worth a call; missing to a delegated access, which never reads who did what (ADR-300 §4). */
    worthACall: z.array(worthACallDto).optional(),
  })
  .meta({ id: "CrmSendReport" });
export type CrmSendReportDto = z.output<typeof crmSendReportDto>;

/** The stages of the funnel of a send, from the email to the order; `visited` and `ordered` wait for P8.14k. */
export const sendFunnelStages = [
  "sent",
  "delivered",
  "linkOpened",
  "slotsViewed",
  "booked",
  "visited",
  "ordered",
] as const;
export type SendFunnelStage = (typeof sendFunnelStages)[number];

/** A stage of the funnel: its count, or null while what it counts is not attributed to a send yet. */
export interface SendFunnelStep {
  stage: SendFunnelStage;
  value: number | null;
}

/**
 * The funnel of a send, from the email to the order (ADR-315 §3). The booking button never goes through the redirect
 * (ADR-313 §5), so the clicks are no stage of it: they are a tile of their own. The appointments kept and the orders
 * are attributed to a send with P8.14k: until then their stages are pending, never a zero.
 */
export function sendFunnel(progress: Pick<SendProgressDto, "sentCount">, totals: SendTotalsDto): SendFunnelStep[] {
  const values: Record<SendFunnelStage, number | null> = {
    sent: progress.sentCount,
    delivered: totals.delivered,
    linkOpened: totals.linkOpened,
    slotsViewed: totals.slotsViewed,
    booked: totals.booked,
    visited: null,
    ordered: null,
  };
  return sendFunnelStages.map((stage) => ({ stage, value: values[stage] }));
}
